import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Cloud, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const LiveWordCloud = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [wordData, setWordData] = useState({ words: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchWordData();
  }, [filters, timeRange]);

  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchWordData();
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, timeRange]);

  const fetchWordData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/word-cloud?time_range=${timeRange}`);
      const result = await response.json();
      setWordData(result);
    } catch (error) {
      console.error('Error fetching word cloud:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFontSize = (value, maxValue) => {
    const minSize = 14;
    const maxSize = 48;
    const ratio = value / maxValue;
    return Math.floor(minSize + (maxSize - minSize) * ratio);
  };

  const getColor = (index) => {
    const colors = [
      '#C9A24A', // gold
      '#6B7280', // ink-500
      '#10b981', // emerald
      '#f59e0b', // amber
      '#ec4899', // pink
      '#14b8a6', // teal
      '#9CA3AF', // ink-400
    ];
    return colors[index % colors.length];
  };

  const maxValue = Math.max(...(wordData.words?.map(w => w.value) || [1]));

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
              <Cloud className="w-5 h-5 text-gold" />
              Live Word Cloud
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Top keywords from call transcripts • Size = frequency
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`text-xs ${autoRefresh ? 'bg-gold/100/20 border-gold/40' : ''}`}
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              Auto {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Time Range Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { key: '1h', label: 'Last Hour' },
            { key: '24h', label: 'Last 24h' },
            { key: '7d', label: 'Last 7d' }
          ].map((range) => (
            <button
              key={range.key}
              onClick={() => setTimeRange(range.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === range.key
                  ? 'bg-gold text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Loading word cloud...</div>
          </div>
        ) : wordData.words.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No transcript data available yet</p>
            <p className="text-xs mt-2">Words will appear as calls are transcribed</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: animationDuration }}
          >
            <div className="relative h-96 flex flex-wrap items-center justify-center gap-4 overflow-hidden">
              {wordData.words.map((word, index) => {
                const fontSize = getFontSize(word.value, maxValue);
                const color = getColor(index);
                
                return (
                  <motion.span
                    key={word.text}
                    className="font-bold cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      fontSize: `${fontSize}px`,
                      color: color,
                      textShadow: `0 0 10px ${color}40`
                    }}
                    initial={reducedMotion ? {} : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.02,
                      ease: 'easeOut'
                    }}
                    whileHover={reducedMotion ? {} : { scale: 1.1 }}
                    title={`${word.text}: ${word.value} mentions`}
                  >
                    {word.text}
                  </motion.span>
                );
              })}
            </div>

            {/* Stats */}
            {wordData.meta && (
              <div className="mt-8 grid grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold">{wordData.words.length || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Unique Words</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{wordData.meta.total_words || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Total Words</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{wordData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{wordData.meta.time_range || '24h'}</p>
                  <p className="text-xs text-slate-400 mt-1">Time Range</p>
                </div>
              </div>
            )}

            {autoRefresh && (
              <div className="mt-4 text-center">
                <p className="text-xs text-slate-500">
                  Auto-refreshing every 10 seconds
                </p>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveWordCloud;
